import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #inactiveViewObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#observeInactiveViewSync();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#inactiveViewObserver?.disconnect();
  }

  // Sync the inactive PLP view with the paginating one (only the default view paginates); observe the stable host (the grid node is replaced on filter/sort) and react only to cards added into the current grid (`grid.contains` guard avoids a feedback loop).
  #observeInactiveViewSync() {
    this.#inactiveViewObserver = new MutationObserver((mutations) => {
      const grid = this.refs.grid;
      if (!grid) return;

      const addedPages = new Set();

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (
            node.nodeType === Node.ELEMENT_NODE &&
            node.dataset?.productId &&
            node.dataset.page &&
            grid.contains(node)
          ) {
            addedPages.add(Number(node.dataset.page));
          }
        }
      }

      for (const page of addedPages) this.#syncInactiveView(page);
    });

    this.#inactiveViewObserver.observe(this, { childList: true, subtree: true });
  }

  // Append `page`'s cards to the non-paginating view from the cached page HTML.
  #syncInactiveView(page) {
    const { grid } = this.refs;
    const paginatingView = grid?.closest('.js-plp-view')?.dataset.view;
    if (!paginatingView) return;

    const inactiveView = paginatingView === '5' ? '3' : '5';
    const inactiveViewEl = this.querySelector(`.js-plp-view[data-view="${inactiveView}"]`);
    if (!inactiveViewEl) return;

    // Append to the last grid (the main one, after the promo in the 3-grid+promo page-1 layout).
    const grids = inactiveViewEl.querySelectorAll('.js-ls-plp-grid');
    const targetGrid = grids[grids.length - 1];
    if (!targetGrid) return;

    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const sourceView = new DOMParser()
      .parseFromString(pageHTML, 'text/html')
      .querySelector(`.js-plp-view[data-view="${inactiveView}"]`);
    if (!sourceView) return;

    const existing = new Set(
      [...inactiveViewEl.querySelectorAll('[data-product-id]')].map((el) => el.dataset.productId)
    );

    const newCards = [...sourceView.querySelectorAll('.js-ls-plp-grid > [data-product-id]')].filter(
      (el) => !existing.has(el.dataset.productId)
    );

    targetGrid.append(...newCards);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
